import { useEffect, useRef, useState } from "react";
import { PaperPlaneTilt, Compass, Sparkle, SpeakerHigh, Pause } from "@phosphor-icons/react";
import { API, getToken, api } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";

const SUGGESTIONS = [
  "What questions should I ask my OB-GYN about endometriosis?",
  "What have women similar to me tried after two failed IVF cycles?",
  "How do I prepare emotionally for my egg retrieval?",
  "What usually happens after a Stage 4 laparoscopy?",
];

export default function Navigator() {
  usePageMeta({ title: "AI Navigator", description: "A warm, evidence-informed AI companion for women's reproductive-health questions. Not medical advice." });
  const [sessionId] = useState(() => `s-${Date.now()}`);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi. I'm here whenever you want to talk. What's on your heart today?" }
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [ttsIdx, setTtsIdx] = useState(null);   // which message is currently playing
  const [ttsLoading, setTtsLoading] = useState(null);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const toggleListen = async (idx, text) => {
    // stop current
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (ttsIdx === idx) { setTtsIdx(null); return; }
    setTtsLoading(idx);
    try {
      const { data } = await api.post("/tts", { text });
      const audio = new Audio(data.audio_url);
      audioRef.current = audio;
      audio.onended = () => { setTtsIdx(null); audioRef.current = null; };
      await audio.play();
      setTtsIdx(idx);
    } catch (e) {
      // silent fail, could toast, but avoid noise
    }
    setTtsLoading(null);
  };

  const send = async (text) => {
    const msg = text ?? input;
    if (!msg.trim() || streaming) return;
    setInput("");
    setMessages(m => [...m, { role: "user", text: msg }, { role: "assistant", text: "", streaming: true }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/nav/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          try {
            const j = JSON.parse(line);
            if (j.delta) {
              setMessages(m => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, text: last.text + j.delta };
                return copy;
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "I couldn't reach the navigator. Please try again." };
        return copy;
      });
    } finally {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false };
        return copy;
      });
      setStreaming(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-8">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-3"><span className="rule" />AI Health Navigator</div>
        <h1 className="font-display text-4xl leading-tight flex items-center gap-3">
          <Compass size={38} weight="duotone" className="text-coral-700" />
          A conversation, not a prescription.
        </h1>
        <p className="mt-3 text-ink/70 max-w-xl">Warm, evidence-informed peer guidance. Not medical advice.</p>

        <div className="mt-8 bg-white border border-border rounded-2xl shadow-card overflow-hidden flex flex-col h-[560px]">
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-6 wing-scroll space-y-5"
            data-testid="chat-scroll"
            role="log"
            aria-live="polite"
            aria-relevant="additions text"
            aria-label="AI Health Navigator conversation"
          >
            {messages.map((m, idx) => (
              <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                  m.role === "user"
                    ? "bg-coral text-white rounded-br-md"
                    : "bg-sage-50 text-ink rounded-bl-md border border-sage-100"
                }`} data-testid={`chat-msg-${m.role}`}>
                  <div className="whitespace-pre-wrap leading-relaxed text-sm">
                    {m.text}{m.streaming && <span className="caret" />}
                  </div>
                  {m.role === "assistant" && !m.streaming && m.text && (
                    <button
                      onClick={() => toggleListen(idx, m.text)}
                      disabled={ttsLoading === idx}
                      data-testid={`tts-btn-${idx}`}
                      aria-label={ttsIdx === idx ? "Pause" : "Listen aloud"}
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-sage-700 hover:text-coral-700 transition-colors disabled:opacity-60"
                    >
                      {ttsLoading === idx
                        ? <><span className="w-3 h-3 border-2 border-sage-300 border-t-sage-700 rounded-full animate-spin" /> Loading…</>
                        : ttsIdx === idx
                          ? <><Pause size={12} weight="fill" /> Pause</>
                          : <><SpeakerHigh size={12} weight="duotone" /> Listen</>}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
                className="border-t border-border p-4 flex items-center gap-3 bg-cream">
            <input
              value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything…"
              data-testid="chat-input"
              disabled={streaming}
              className="flex-1 bg-white border border-border rounded-full px-5 py-3 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none disabled:opacity-60"
            />
            <button type="submit" disabled={streaming || !input.trim()}
                    data-testid="chat-send"
                    aria-label="Send message"
                    className="bg-coral text-white p-3 rounded-full hover:bg-coral-600 disabled:opacity-50 transition-colors">
              <PaperPlaneTilt size={20} weight="fill" aria-hidden="true" />
            </button>
          </form>
        </div>
      </div>

      <aside className="lg:col-span-4 space-y-6">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-600"><span className="rule" />Try asking</div>
        <div className="space-y-2 stagger">
          {SUGGESTIONS.map((s, i) => (
            <button key={i} onClick={() => send(s)} disabled={streaming}
                    data-testid={`suggestion-${i}`}
                    className="w-full text-left p-4 bg-white border border-border rounded-xl hover:border-coral transition-colors disabled:opacity-60">
              <Sparkle size={16} weight="duotone" className="text-sage inline mr-2" />
              <span className="text-sm">{s}</span>
            </button>
          ))}
        </div>
        <div className="text-xs text-muted-foreground leading-relaxed p-4 bg-muted rounded-xl">
          The Navigator listens and educates. It does not diagnose or prescribe.
          For medical decisions, please speak with a clinician.
        </div>
      </aside>
    </div>
  );
}
